/**
 * Order queue logic, kept pure so the rules are testable without rendering.
 *
 * The transitions here mirror ALLOWED_TRANSITIONS in the order service's domain. They are
 * duplicated deliberately — the console must not offer a button the backend will reject —
 * and a test asserts every action it offers is one the server accepts.
 */

export type OrderState =
  | "BUILDING"
  | "SUBMITTED"
  | "ACCEPTED"
  | "PREPARING"
  | "READY"
  | "COMPLETED"
  | "REJECTED"
  | "CANCELLED"
  | "EXPIRED";

export interface Money {
  amountMinor: number;
  currency: string;
  display?: string;
}

export interface OrderLine {
  nameSnapshot: string;
  quantity: number;
  unitPrice: Money;
  /**
   * Names as stored on the order. Typed string, but orders placed before 2026-08-21 hold the published
   * menu's { en, zh } object here, because the order service cast those names instead of flattening
   * them. Render with modifierLabel, never directly.
   */
  modifiers: (string | { en?: string; zh?: string })[];
}

export interface Order {
  orderId: string;
  state: OrderState;
  customerName?: string;
  customerPhone?: string;
  lines: OrderLine[];
  total: Money;
  submittedAtIso?: string;
  estimatedReadyMinutes?: number;
  readyAtIso?: string;
}

/** Orders the restaurant still has work to do on. */
export const ACTIVE_STATES: readonly OrderState[] = [
  "SUBMITTED",
  "ACCEPTED",
  "PREPARING",
  "READY"
];

export function isActive(state: OrderState): boolean {
  return ACTIVE_STATES.includes(state);
}

/**
 * Drafts never submitted are not orders as far as the restaurant is concerned; they are
 * abandoned carts and must not appear in either list.
 */
export function isVisibleToStaff(state: OrderState): boolean {
  return state !== "BUILDING" && state !== "EXPIRED";
}

export interface QueueAction {
  /** State to send to POST /orders/{id}/status */
  to: OrderState;
  label: string;
  /** Destructive actions get a confirmation and a muted style. */
  danger?: boolean;
}

/** What staff can do next with an order in this state, in the order to show them. */
export function actionsFor(state: OrderState): QueueAction[] {
  switch (state) {
    case "SUBMITTED":
      return [
        { to: "ACCEPTED", label: "Accept" },
        { to: "REJECTED", label: "Reject", danger: true }
      ];
    case "ACCEPTED":
      return [
        { to: "PREPARING", label: "Start preparing" },
        { to: "CANCELLED", label: "Cancel", danger: true }
      ];
    case "PREPARING":
      return [
        { to: "READY", label: "Mark ready" },
        { to: "CANCELLED", label: "Cancel", danger: true }
      ];
    case "READY":
      return [{ to: "COMPLETED", label: "Picked up" }];
    default:
      return []; // terminal
  }
}

const LABELS: Record<OrderState, string> = {
  BUILDING: "In progress",
  SUBMITTED: "New",
  ACCEPTED: "Accepted",
  PREPARING: "Preparing",
  READY: "Ready",
  COMPLETED: "Picked up",
  REJECTED: "Rejected",
  CANCELLED: "Cancelled",
  EXPIRED: "Abandoned"
};

/** "SUBMITTED" is internal wording; staff see "New". */
export function stateLabel(state: OrderState): string {
  return LABELS[state] ?? state;
}

export interface GroupedOrders {
  active: Order[];
  completed: Order[];
}

/**
 * Split into the two lists staff asked for.
 *
 * Active is sorted OLDEST FIRST: the order waiting longest needs attention first, and a
 * newest-first queue would bury it. Completed is newest first, since that is history.
 */
export function groupOrders(orders: readonly Order[]): GroupedOrders {
  const visible = orders.filter((o) => isVisibleToStaff(o.state));
  const active = visible.filter((o) => isActive(o.state));
  const completed = visible.filter((o) => !isActive(o.state));
  return {
    active: [...active].sort(bySubmittedAt("asc")),
    completed: [...completed].sort(bySubmittedAt("desc"))
  };
}

function bySubmittedAt(dir: "asc" | "desc") {
  return (a: Order, b: Order): number => {
    const ta = a.submittedAtIso ? Date.parse(a.submittedAtIso) : 0;
    const tb = b.submittedAtIso ? Date.parse(b.submittedAtIso) : 0;
    return dir === "asc" ? ta - tb : tb - ta;
  };
}

/** Whole minutes since the order was placed, for the "waiting 12m" badge. */
export function minutesSince(iso: string | undefined, now = Date.now()): number | undefined {
  if (!iso) return undefined;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return undefined;
  return Math.max(0, Math.floor((now - t) / 60_000));
}

/**
 * Is this order past the pickup time the caller was promised?
 *
 * Only meaningful while the order is still active — a completed order being "late" is
 * noise, not something staff can act on.
 */
export function isOverdue(order: Order, now = Date.now()): boolean {
  if (!isActive(order.state)) return false;
  if (!order.readyAtIso) return false;
  const t = Date.parse(order.readyAtIso);
  return !Number.isNaN(t) && now > t;
}

/** Total units, for an at-a-glance "6 items" on the card. */
export function itemCount(order: Order): number {
  return order.lines.reduce((n, l) => n + l.quantity, 0);
}

/**
 * What staff see to match a waiting customer.
 *
 * The name is the human interface — it is what gets called out — but it is transcribed from speech and
 * can be confidently wrong, especially for names that are not English. The last four digits of the
 * caller's own number cannot be: they come from Twilio, not from listening. Showing both gives staff a
 * second key when the customer says a name that does not match the screen.
 *
 * Only the last four, never the whole number: a queue on a counter tablet can face the room.
 */
export function pickupLabel(order: Order): string {
  const digits = order.customerPhone?.replace(/\D/g, "");
  const last4 = digits && digits.length >= 4 ? digits.slice(-4) : undefined;
  if (order.customerName && last4) return `${order.customerName} · ${last4}`;
  if (order.customerName) return order.customerName;
  // No name: the digits ARE the label, not a decoration on a missing one.
  if (last4) return `Order ${last4}`;
  return "Phone order";
}

/**
 * One modifier name, safe for an order of any age.
 *
 * New orders store a flat string. Orders placed before the flattening fix hold the published menu's
 * { en, zh } object, which rendered as "[object Object]" on the queue — visible to staff on real orders,
 * so the console tolerates both rather than waiting for the old rows to age out.
 */
export function modifierLabel(value: string | { en?: string; zh?: string }): string {
  if (typeof value === "string") return value;
  return [value?.en, value?.zh].filter((p): p is string => Boolean(p)).join(" ");
}
